<template>
  <v-container
    fluid
    class="order-history">
    <!-- META-DATA -->
    <vue-headful
      :title="'Order History' + this.$store.state.app.meta.titleSuffix"
      :description="null" />

    <v-data-table
      :items="orderHistory"
      :loading="loading"
      :headers="headers"
      :pagination.sync="pagination"
      item-key="cartId"
      sort-icon="expand_less"
      class="order-history-table">
      <template slot="no-data">
        <v-alert
          outline
          :value="true"
          color="primary"
          icon="warning">
          <span v-if="errorMessage">{{ errorMessage }}</span>
          <span v-else>You haven't placed any orders yet</span>
        </v-alert>
      </template>

      <template
        slot="items"
        slot-scope="props">
        <tr
          v-ripple
          class="order-history-row"
          :class="{ 'order-history-row--active': props.expanded }"
          @click="props.expanded = !props.expanded">
          <td class="order-history-row__date-ordered">
            {{ moment(props.item.dateStamp).format('MMM Do, YYYY') }}
          </td>
          <td class="order-history-row__order-number">
            <a @click.prevent>{{ props.item.orderNumber }}</a>
          </td>
          <td class="order-history-row__total">
            {{ props.item.total | price }}
          </td>
          <td class="order-history-row__status">
            {{ props.item.status }}
          </td>
        </tr>
      </template>

      <!-- EXPANDED ORDER DETAILS -->
      <template
        v-slot:expand="props">
        <div class="invoice">
          <div class="invoice__header">
            <div class="invoice__order-number">
              <span class="invoice__subheader">order number:</span>
              <span>{{ props.item.orderNumber }}</span>
            </div>

            <div class="invoice__order-date">
              <span class="invoice__subheader">order date:</span>
              <span>{{ moment(props.item.dateStamp).format('MMM Do, YYYY') }}</span>
            </div>

            <div class="invoice__shipping-address">
              <span class="invoice__subheader">shipping address:</span>
              <div class="address">
                <span class="address__name">
                  {{ props.item.shippingAddress.fullName }}
                </span>
                <span class="address__company">
                  {{ props.item.shippingAddress.company }}
                </span>
                <span class="address__address1">
                  {{ props.item.shippingAddress.line1 }}
                </span>
                <span class="address__address2">
                  {{ props.item.shippingAddress.line2 }}
                </span>
                <div class="address__city-state-zip">
                  <span class="address__city">
                    {{ props.item.shippingAddress.city }},
                  </span>
                  <span class="address__state">
                    {{ props.item.shippingAddress.state }}
                  </span>
                  <span class="address__zip">
                    {{ props.item.shippingAddress.zipcode }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ITEMS -->
          <ul class="invoice__list-items">
            <!-- hats -->
            <order-product
              v-for="hat in props.item.hats"
              :key="hat.slug"
              :item="hat"
              item-set="hats" />
            <!-- patches -->
            <order-product
              v-for="patch in props.item.patches"
              :key="patch.imageId"
              :item="patch"
              item-set="patches" />
            <!-- customs -->
            <order-product
              v-for="custom in props.item.customPatches"
              :key="custom.imageId"
              :item="custom"
              item-set="customs" />
          </ul>

          <!-- ORDER FOOTER/TOTALS -->
          <div class="invoice__footer">
            <ul class="invoice-totals">
              <li class="invoice-totals__subtotal">
                <span>subtotal</span>
                <span>{{ props.item.subtotal | price }}</span>
              </li>
              <li
                v-if="props.item.discount && props.item.discount!==0"
                class="invoice-totals__discount">
                <span>
                  total discount
                </span>
                <span>
                  -{{ props.item.discount | price }}
                </span>
              </li>
              <li class="invoice-totals__shipping">
                <span>shipping</span>
                <span>free!</span>
              </li>
              <li
                v-if="props.item.salesTax && props.item.salesTax!=='0'"
                class="invoice-totals__taxes">
                <span>Sales Tax</span>
                <span v-if="props.item.salesTax">{{ props.item.salesTax | price }}</span>
                <span v-if="!props.item.salesTax">None</span>
              </li>
              <li class="invoice-totals__total">
                <!-- [!] Actual totals, calculated on the server -->
                <span>
                  total
                </span>
                <span>
                  {{ props.item.total | price }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

import OrderProduct from '../../components/account/OrderProduct.vue';

export default {
  name: 'OrderHistory',
  components: {
    OrderProduct,
  },
  data() {
    return {
      errorMessage: '',
      headers: [
        {
          text: 'Order Date',
          value: 'dateStamp',
        },
        {
          text: 'Order Number',
          value: 'orderNumber',
        },
        {
          text: 'Order Total',
          value: 'total',
        },
        {
          text: 'Order Status',
          value: 'status',
        },
      ],
      pagination: {
        descending: true,
        sortBy: 'dateStamp',
      },
    };
  },
  computed: {
    ...mapState('user', {
      loading: 'loading',
      currentUser: 'currentUser',
      orderHistory: 'orderHistory',
    }),
  },
  created() {
    this.$store
      .dispatch('user/getAllOrders', this.currentUser.publicId)
      .then(this.clearError())
      .catch(error => this.errorMessage = error);
  },
  methods: {
    clearError() { this.errorMessage = ''; },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.order-history-table
  z-index: 1
  border 1px solid $accent
  border-radius: $base-border-radius

.order-history-row
  position: relative
  cursor: pointer;
  &::after
    content: ''
    position: absolute
    top: 0
    left: 0
    height: 100%
    width: 100%
    z-index: 2
    background-color: $black
    opacity: 0
    transition: $base-transition
    cursor: pointer
  &:hover::after
    opacity: .24
  &--active
    position: relative
    &::after
      opacity: .12

.invoice
  padding: $base-padding
  &__header
    display: flex
    flex-flow: column nowrap
    border-bottom: 1px solid $accent
  &__subheader
    @extends $base-subheader
  &__shipping-address
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    padding-bottom: $base-padding*2
  &__payment-method
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    align-items: flex-end
    padding-bottom: $base-padding*2
  &__list-items
    list-style: none
    padding: 0
    padding-top: $base-padding
    padding-bottom: $base-padding*.5
  &__footer
    display: flex
    flex-flow: row nowrap
    align-items: stretch;
    justify-content: space-between
    padding-top: $base-padding
    border-top: 1px solid $accent
    @media screen and (max-width: 48rem)
      flex-direction: column-reverse

.address
  display: flex
  flex-flow: column nowrap
  text-transform: capitalize
  &__state
    text-transform: uppercase

.invoice-totals
  flex: 1 1 0
  list-style: none
  padding: 0
  padding-top: $base-padding
  @media screen and (max-width: 48rem)
    flex-basis: 100%
  &__subtotal,
  &__discount,
  &__total
    display: flex
    justify-content: space-between
    height: 1.5rem
    width: 100%
    font-weight: $base-font-weight
    font-size: 1rem
    text-transform: capitalize
  &__total
    border-top: 1px solid $accent

.invoice-totals
  flex: 1 1 0
  list-style: none
  padding: 0
  @media screen and (max-width: 48rem)
    flex-basis: 100%
  &__subtotal,
  &__discount,
  &__shipping,
  &__taxes,
  &__total
    display: flex
    justify-content: space-between
    height: 3rem
    width: 100%
    padding: $base-padding
    font-weight: $base-font-weight
    font-size: 1rem
    text-transform: capitalize
  &__total
    font-size: 1.5rem
    font-weight: $base-header-font-weight
    border-top: 1px solid $accent

</style>
